import { createElement, ReactElement, RefObject } from "react"
import dayjs from "dayjs"
import timezonePlugin from "dayjs/plugin/timezone"
import utc from "dayjs/plugin/utc"
import { IconProps } from "@/components/shared/Icon"
import { SortDirection } from "@/components/shared/Table/types"
import Tooltip from "@/components/shared/Tooltip"
import { HttpCodes } from "@/constants/globals"
import { AlertLevel } from "@/types/alert"
import { Inmate } from "@/types/inmate"
import {
   compact,
   head,
   isArray,
   isEmpty,
   isNil,
   isObject,
   mapValues,
   pickBy,
   upperFirst,
} from "@/utils/lodash"

dayjs.extend(utc)
dayjs.extend(timezonePlugin)

export function convertCamelToKebab(str: string): string {
   return str.replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase()
}

export function capitalizeFirstLetter(str: string): string {
   return str.charAt(0).toUpperCase() + str.slice(1)
}

/**
 * Safely converts a value to a string, handling null, undefined, and other non-string values.
 * @param value The value to convert to a string
 * @returns A string representation of the value, or an empty string for null/undefined
 */
export function safeToString(value: unknown): string {
   if (value === null || value === undefined) {
      return ""
   }

   if (typeof value === "object") {
      try {
         return JSON.stringify(value)
      } catch {
         return Object.prototype.toString.call(value)
      }
   }

   return String(value)
}

/** Deep cleans an object with empty, null, undefined values recursively. */
export function cleanDeep(data: unknown): unknown {
   if (isArray(data)) {
      return data
         .map(cleanDeep)
         .filter((v) => !isNil(v) && v !== "" && (!isObject(v) || !isEmpty(v)))
   }

   if (isObject(data)) {
      return pickBy(
         mapValues(data, cleanDeep),
         (v) => !isNil(v) && v !== "" && (!isObject(v) || !isEmpty(v))
      )
   }

   return data
}

/**
 * Sorts an array of objects by a specified field in ascending or descending order.
 * Handles empty strings and uses locale-aware string comparison for string values.
 *
 * @template T - The type of objects in the array
 * @param {T[]} items - The array of objects to sort
 * @param {keyof T} field - The object key to sort by
 * @param {SortDirection} [direction=SortDirection.ASC] - The sort direction, ascending or descending
 * @returns {T[]} A new array sorted by the specified field and direction
 */
const collator = new Intl.Collator(undefined, {
   numeric: true,
   sensitivity: "base",
})

export function sortByField<T>(
   items: T[],
   field: keyof T,
   direction: SortDirection = SortDirection.ASC
): T[] {
   const compareValues = <T>(
      aValue: T[keyof T],
      bValue: T[keyof T],
      direction: SortDirection
   ): number => {
      let comparison = 0

      if (aValue === "" && bValue === "") return 0
      if (aValue === "") return 1
      if (bValue === "") return -1
      if (aValue === bValue) return 0

      if (typeof aValue === "string" && typeof bValue === "string") {
         comparison = collator.compare(aValue, bValue)
      } else {
         comparison = aValue < bValue ? -1 : 1
      }

      return direction === SortDirection.ASC ? comparison : -comparison
   }

   return [...items].sort((a, b) =>
      compareValues(a[field], b[field], direction)
   )
}

export function getErrorMessage(error: number, customMessage?: string): string {
   const message = customMessage ?? "Please refresh the page and try again"
   switch (error) {
      case HttpCodes.BAD_REQUEST:
         return message
      default:
         return "An error occurred"
   }
}

export function getLocale(): string {
   const locale =
      navigator.languages && navigator.languages.length
         ? navigator.languages[0]
         : navigator.language || "en"

   return locale
}

export function getBrowserTimezone(): string {
   return Intl.DateTimeFormat().resolvedOptions().timeZone
}

// Intl throws on an unknown IANA identifier, and so does dayjs .tz()
export function isValidTimezone(timezone: string): boolean {
   try {
      new Intl.DateTimeFormat(undefined, { timeZone: timezone })
      return true
   } catch {
      return false
   }
}

export function toFacilityTime(
   dateTime: dayjs.Dayjs,
   timezone: string | null
): dayjs.Dayjs {
   return timezone ? dateTime.tz(timezone) : dateTime
}

export function localizeDate(
   dateTime: dayjs.Dayjs,
   timezone: string | null
): string {
   const locale = getLocale()

   const isUS = locale.startsWith("en-US")
   return toFacilityTime(dateTime, timezone)
      .locale(locale)
      .format(isUS ? "MM-DD-YYYY" : "DD-MM-YYYY")
}

export function localizeDateTime(
   dateTime: dayjs.Dayjs,
   timezone: string | null
): string {
   if (!dateTime) return ""

   const locale = getLocale()

   const isUS = locale.startsWith("en-US")
   return toFacilityTime(dateTime, timezone)
      .locale(locale)
      .format(isUS ? "MM-DD-YYYY hh:mm A" : "DD-MM-YYYY HH:mm")
}

export function isShowingEllipsis(currentItem: HTMLElement): boolean {
   return currentItem.scrollWidth > currentItem.clientWidth
}

export function extractParents(
   parents?: AlertLevel[] | null
): { name: string; type: string }[] {
   if (!Array.isArray(parents) || !parents.length) return []
   return parents.flatMap((parent) => [
      { name: parent.name, type: parent.type?.name ?? "" },
      ...extractParents(parent.parents),
   ])
}

export function normalizeMacAddress(macAddress: string): string {
   return macAddress.toLowerCase().replace(/[:\-.]/g, "")
}

export function getBatteryChargeIcon(charge: number): IconProps["name"] {
   if (charge > 75) return "batteryFull"
   if (charge >= 50) return "batteryHalf"
   return "batteryLow"
}

export function renderWithTooltip(
   ref: RefObject<HTMLSpanElement | null>,
   className: string | undefined,
   content: string | undefined,
   isTruncated: boolean
): ReactElement {
   const span = createElement("span", { ref, className }, content)
   return isTruncated
      ? (createElement(Tooltip, { content } as never, span) as ReactElement)
      : span
}

export const getInitial = (name: string) => {
   return `${upperFirst(head(name.trim()))}.`
}

export function formatInmateName(
   inmate: Partial<Pick<Inmate, "firstName" | "middleName" | "lastName">> | null
): string {
   if (!inmate) return ""

   const parts: string[] = []

   if (inmate.firstName) {
      parts.push(inmate.firstName)
   }

   if (inmate.middleName) {
      parts.push(getInitial(inmate.middleName))
   }

   if (inmate.lastName) {
      parts.push(inmate.lastName)
   }

   return parts.join(" ")
}

export function getFullInmateInfo(
   inmate:
      | Partial<
           Pick<
              Inmate,
              "firstName" | "middleName" | "lastName" | "bookingNumber"
           >
        >
      | null
      | undefined
): string {
   if (!inmate) return ""
   const formattedBookingNumber =
      inmate.bookingNumber != null ? `(${inmate.bookingNumber})` : undefined
   return compact([formatInmateName(inmate), formattedBookingNumber]).join(" ")
}
